import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, formatErr } from "@/lib/api";
import { money } from "@/pages/Awards";
import { toast } from "sonner";

const SUB_GRANTS = { proposal_writer_monthly: ["proposal_writer"], licensing_guide_monthly: ["licensing_guide"], verify_suite_monthly: ["proposal_writer", "licensing_guide", "suite"] };
const PKGS = [["proposal_writer", "Proposal Writer"], ["licensing_guide", "State Licensing"], ["suite", "Full Verify Suite"]];

function Section({ title, children }) {
  return <div className="mt-6"><h4 className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">{title}</h4>{children}</div>;
}

export function UserDrawer({ userId, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [pk, setPk] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!userId) return;
    setD(null);
    api.get(`/admin/users/${userId}`).then((r) => {
      const fromSubs = new Set(r.data.subscriptions.filter((s) => s.status === "active").flatMap((s) => SUB_GRANTS[s.lookup_key] || []));
      setD(r.data);
      setPk(r.data.user.manual_packages ?? (r.data.user.packages || []).filter((p) => !fromSubs.has(p)));
    });
  }, [userId]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/users/${userId}/packages`, { manual_packages: pk }); toast.success("Packages updated"); onSaved(userId, data.packages); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const subPkgs = new Set((d?.subscriptions || []).filter((s) => s.status === "active").flatMap((s) => SUB_GRANTS[s.lookup_key] || []));
  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-user-drawer">
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600 mt-10" /> : (
          <>
            <SheetHeader><SheetTitle className="font-display text-2xl">{d.user.name}</SheetTitle></SheetHeader>
            <p className="text-sm text-slate-500">{d.user.email} · {d.user.auth_provider} · joined {new Date(d.user.created_at).toLocaleDateString()}</p>
            <p className="text-sm text-slate-600 mt-1">{[d.profile.company_name, d.profile.city, d.profile.state, d.profile.industry].filter(Boolean).join(" · ") || "No business profile yet"}</p>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              {[["Guides", d.counts.licensing_guides], ["Documents", d.counts.documents], ["Team", d.counts.members]].map(([l, v]) => <div key={l} className="bg-slate-50 rounded p-2"><div className="font-bold text-slate-900">{v}</div><div className="text-[10px] text-slate-500 uppercase">{l}</div></div>)}
            </div>
            <Section title="Packages">
              <div className="space-y-2">
                {PKGS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={pk.includes(k) || subPkgs.has(k)} disabled={subPkgs.has(k)} onCheckedChange={(v) => setPk(v ? [...pk, k] : pk.filter((x) => x !== k))} data-testid={`drawer-pkg-${k}`} />{l}{subPkgs.has(k) && <span className="text-[10px] font-mono uppercase text-emerald-600">via subscription</span>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Ticked boxes are manual grants. Packages from active Stripe subscriptions turn off automatically on cancellation or failed payment.</p>
              <Button size="sm" onClick={save} disabled={busy} className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="drawer-save-packages-button">{busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save packages</Button>
            </Section>
            <Section title={`Subscriptions (${d.subscriptions.length})`}>
              {d.subscriptions.map((s) => <div key={s.subscription_id} className="text-sm flex justify-between py-1"><span>{s.lookup_key}</span><span className={s.status === "active" ? "text-emerald-600" : "text-red-600"}>{s.status}{s.cancel_at_period_end ? " · cancels at period end" : ""}</span></div>)}
            </Section>
            <Section title={`Payments (${d.payments.length})`}>
              {d.payments.map((p) => <div key={p.session_id} className="text-sm flex justify-between py-1" data-testid={`drawer-payment-${p.session_id}`}><span className="text-slate-700">{p.lookup_key}</span><span className={p.payment_status === "paid" ? "text-slate-900 font-semibold" : "text-slate-400"}>{money(p.amount)} · {p.payment_status}</span></div>)}
            </Section>
            <Section title={`Proposals (${d.proposals.length})`}>
              {d.proposals.map((p) => <div key={p.proposal_id} className="text-sm py-1 text-slate-700">{p.title}</div>)}
            </Section>
            <Section title={`Human reviews (${d.reviews.length})`}>
              {d.reviews.map((r) => <div key={r.review_id} className="text-sm flex justify-between py-1"><span>{r.proposal_title}</span><span className="text-amber-700">{r.status}</span></div>)}
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
